import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, Grid2, InputLabel, MenuItem, Modal, Select, TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { DateField } from "@mui/x-date-pickers/DateField";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../Auth/AuthContext";
import { Link, useNavigate } from "react-router-dom";

const API = "https://api.sigmasystems.com.br/api/ag-barber";

const style = {
  position: "absolute",
  top:  { xs: "50%", md: "50%" },
  left: { xs: "50%", md: "50%" },
  transform: { xs: "translate(-50%, -50%)", md: "translate(-50%, -50%)" },
  width:     { xs: "95vw", md: 815 },
  height:    { xs: "95vh", md: "auto" },
  maxHeight: { xs: "95vh", md: "90vh" },
  overflowY: "auto",
  boxSizing: "border-box",
  bgcolor: "background.paper",
  border: { xs: "none", md: "2px solid #4A7011" },
  borderRadius: { xs: 0, md: "8px" },
  boxShadow: 24,
  p: { xs: 2, md: 4 },
};

const btnPrimario = {
  color: "#fff", height: 40, width: 110, borderRadius: "5px", fontSize: "15px", fontWeight: "bold",
  backgroundColor: "#4a7011", textTransform: "none", "&:hover": { backgroundColor: "#293D09" },
};
const btnSecundario = {
  color: "#4a7011", height: 40, width: 110, border: "solid 2px #4a7011", borderRadius: "5px",
  fontSize: "15px", fontWeight: "bold", backgroundColor: "transparent", textTransform: "none",
  "&:hover": { backgroundColor: "#eeeeee" },
};
const btnBlock = {
  color: "#ff000080", height: 40, width: 110, border: "solid 2px #ff000070", borderRadius: "5px",
  fontSize: "15px", fontWeight: "bold", backgroundColor: "transparent", textTransform: "none",
  "&:hover": { backgroundColor: "#ff000020" },
};

const DIAS_SEMANA = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];
const DIAS_EXTENSO = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const FORM_VAZIO = {
  nome: "", descricao: "", telefone: "",
  servico_id: "", profissional_id: "",
  dia: null, hora: null,
};

const BLOQUEIO_VAZIO = {
  profissional_id: "",   // "ALL" = toda a barbearia (só ADMIN)
  de: null, ate: null,   // "ate" vazio = um dia só
  diaInteiro: true, inicio: null, fim: null,
  motivo: "",
};

/* ---------- helpers ---------- */

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const dataBR = (iso) => String(iso).split("-").reverse().join("/");

// "YYYY-MM-DD" -> o dia seguinte (meio-dia evita virar de dia por fuso/horário de verão)
const proximoDia = (key) => {
  const d = new Date(`${key}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return toKey(d);
};

// "Horário bloqueado das 08:00 até 12:00" / "Dia bloqueado (12/11/2026 a 14/11/2026)"
const textoBloqueio = (b) => {
  const faixa = b.date_to !== b.date_from ? ` (${dataBR(b.date_from)} a ${dataBR(b.date_to)})` : "";
  return b.start_time
    ? `Horário bloqueado das ${b.start_time} até ${b.end_time}${faixa}`
    : `Dia bloqueado${faixa}`;
};

// só os dígitos do telefone (tira o +55 se vier colado)
const soDigitos = (v) => {
  let d = String(v ?? "").replace(/\D/g, "");
  if ((d.length === 12 || d.length === 13) && d.startsWith("55")) d = d.slice(2);
  return d.slice(0, 11);
};
// (99) 9999-9999 ou (99) 99999-9999, formatando enquanto digita
function mascaraTelefone(valor) {
  const d = soDigitos(valor);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}
// para exibir na lista: telefone completo vira máscara; texto antigo fora do padrão aparece como está
const formatarFone = (v) => {
  const d = soDigitos(v);
  return d.length === 10 || d.length === 11 ? mascaraTelefone(d) : v;
};

// Chave "YYYY-MM-DD" no horário LOCAL.
// NÃO use toISOString() aqui: ele converte para UTC e, no Brasil (UTC-3),
// depois das 21h o dia vira o dia seguinte.
function toKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Gera as células do mês, incluindo dias do mês anterior/seguinte
// para completar as semanas (grade sempre começa no domingo).
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  const lastDay = new Date(year, month + 1, 0);
  const totalCells = Math.ceil((first.getDay() + lastDay.getDate()) / 7) * 7;

  return Array.from({ length: totalCells }, (_, i) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return { date, key: toKey(date), currentMonth: date.getMonth() === month };
  });
}

const mensagemErro = (error, padrao) => error.response?.data?.message || padrao;
const hhmm = (t) => String(t).slice(0, 5);
// "09:00:00" (vindo do banco) -> Dayjs que o TimeField entende
const horaParaDayjs = (t) => dayjs(`2000-01-01T${t.length === 5 ? `${t}:00` : t}`);

// Quem está logado (o servidor é quem realmente decide as permissões; aqui é só a interface)
function lerSessao() {
  try {
    return jwtDecode(localStorage.getItem("token"));
  } catch {
    return null;
  }
}

function validarAg(f) {
  if (!f.nome.trim()) return "Informe o nome do cliente.";
  if (f.nome.trim().length > 145) return "O nome pode ter no máximo 145 caracteres.";
  const fone = soDigitos(f.telefone);
  if (fone && fone.length < 10) return "Informe o telefone completo, com DDD.";
  if (f.descricao.trim().length > 255) return "A descrição pode ter no máximo 255 caracteres.";
  if (!f.profissional_id) return "Selecione o profissional.";
  if (!f.servico_id) return "Selecione o serviço.";
  if (!f.dia || !f.dia.isValid()) return "Informe o dia.";
  if (!f.hora || !f.hora.isValid()) return "Informe o horário.";
  return null;
}

/* ---------- componente ---------- */

function Agendamentos() {
  const { nome, logout, ehAdmin } = useAuth();
  const navigate = useNavigate();

  const sessao = useMemo(lerSessao, []);
  const meuId = sessao?.id ?? null;

  const hoje = new Date();
  const [mesAtual, setMesAtual] = useState(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
  const [diaSelecionado, setDiaSelecionado] = useState(hoje);
  const [busca, setBusca] = useState("");
  const [fechados, setFechados] = useState({});   // { [idDoProfissional]: true } => recolhido

  // dados vindos da API
  const [profissionais, setProfissionais] = useState([]);   // [{ id, name, role, services: [ids] }]
  const [services, setServices] = useState([]);
  const [contagens, setContagens] = useState({});           // { "2026-10-01": 3 }
  const [agDia, setAgDia] = useState([]);                   // agendamentos do dia selecionado
  const [carregandoDia, setCarregandoDia] = useState(false);
  const [versao, setVersao] = useState(0);                  // aumenta para recarregar calendário e dia

  // modal de adicionar / editar
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);           // agendamento em edição (null = novo)
  const [formAddAg, setFormAddAg] = useState(FORM_VAZIO);
  const [erroAg, setErroAg] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [livres, setLivres] = useState({ carregando: false, fechado: false, horarios: [] });

  // comissão do mês exibido (null = carregando)
  const [comissaoMes, setComissaoMes] = useState(null);

  // modal de bloquear data
  const [openBloq, setOpenBloq] = useState(false);
  const [formBloq, setFormBloq] = useState(BLOQUEIO_VAZIO);
  const [bloqueios, setBloqueios] = useState([]);
  const [erroBloq, setErroBloq] = useState("");
  const [okBloq, setOkBloq] = useState("");
  const [salvandoBloq, setSalvandoBloq] = useState(false);
  const [editandoBloq, setEditandoBloq] = useState(null);   // bloqueio em edição (null = novo)
  const [bloqueiosMes, setBloqueiosMes] = useState([]);     // bloqueios que tocam o calendário exibido

  const ano = mesAtual.getFullYear();
  const mes = mesAtual.getMonth();
  const hojeKey = toKey(hoje);
  const selecionadoKey = toKey(diaSelecionado);

  const grade = useMemo(() => buildMonthGrid(ano, mes), [ano, mes]);
  const de = grade[0].key;
  const ate = grade[grade.length - 1].key;

  /* ----- buscar dados ----- */

  // profissionais (com os serviços de cada um) e serviços
  useEffect(() => {
    (async () => {
      try {
        const [u, s] = await Promise.all([
          axios.post(`${API}/get-users`, {}),
          axios.post(`${API}/get-services`, {}),
        ]);
        setProfissionais(u.data);
        setServices(s.data);
      } catch (error) {
        console.error("Erro ao buscar profissionais e serviços:", error);
      }
    })();
  }, []);

  // quantidade de horários marcados em cada dia do calendário
  useEffect(() => {
    let cancelado = false;
    axios
      .get(`${API}/agenda-month`, { params: { from: de, to: ate } })
      .then(({ data }) => { if (!cancelado) setContagens(data.dias); })
      .catch((error) => console.error("Erro ao buscar o mês:", error));
    return () => { cancelado = true; };
  }, [de, ate, versao]);

  // bloqueios (folgas, feriados...) que tocam o período do calendário
  useEffect(() => {
    let cancelado = false;
    axios
      .get(`${API}/time-off`, { params: { from: de, to: ate } })
      .then(({ data }) => { if (!cancelado) setBloqueiosMes(data); })
      .catch((error) => console.error("Erro ao buscar bloqueios:", error));
    return () => { cancelado = true; };
  }, [de, ate, versao]);

  // comissão do usuário logado no mês exibido: mês passado = total, mês atual = até agora, mês futuro = 0
  useEffect(() => {
    let cancelado = false;
    setComissaoMes(null);
    axios
      .get(`${API}/my-commission`, { params: { month: `${ano}-${String(mes + 1).padStart(2, "0")}` } })
      .then(({ data }) => { if (!cancelado) setComissaoMes(data.total); })
      .catch(() => { if (!cancelado) setComissaoMes(null); });
    return () => { cancelado = true; };
  }, [ano, mes]);

  // agendamentos do dia selecionado
  useEffect(() => {
    let cancelado = false;
    setCarregandoDia(true);
    axios
      .get(`${API}/agenda-day`, { params: { date: selecionadoKey } })
      .then(({ data }) => { if (!cancelado) setAgDia(data); })
      .catch((error) => console.error("Erro ao buscar o dia:", error))
      .finally(() => { if (!cancelado) setCarregandoDia(false); });
    return () => { cancelado = true; };
  }, [selecionadoKey, versao]);

  // horários livres para o profissional, serviço e dia escolhidos no modal
  const diaForm = formAddAg.dia?.isValid() ? formAddAg.dia.format("YYYY-MM-DD") : null;
  useEffect(() => {
    if (!open) return undefined;
    const { profissional_id, servico_id } = formAddAg;
    if (!profissional_id || !servico_id || !diaForm) {
      setLivres({ carregando: false, fechado: false, horarios: [] });
      return undefined;
    }

    let cancelado = false;
    setLivres((l) => ({ ...l, carregando: true }));
    axios
      .get(`${API}/availability`, {
        params: { user_id: profissional_id, service_id: servico_id, date: diaForm, ignore_id: editando?.id },
      })
      .then(({ data }) => {
        if (!cancelado) setLivres({ carregando: false, fechado: data.fechado, horarios: data.livres });
      })
      .catch(() => {
        if (!cancelado) setLivres({ carregando: false, fechado: false, horarios: [] });
      });
    return () => { cancelado = true; };
  }, [open, formAddAg.profissional_id, formAddAg.servico_id, diaForm, editando]);

  /* ----- calendário e lista do dia ----- */

  // filtra pela busca (o telefone só existe para ADMIN, então só entra na busca dele)
  const doDia = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return agDia;
    return agDia.filter((a) =>
      `${a.name} ${a.service_name ?? ""} ${a.obs ?? ""} ${a.phone ?? ""} ${a.professional_name ?? ""}`
        .toLowerCase()
        .includes(termo));
  }, [agDia, busca]);

  // um grupo por profissional ativo; agendamentos de profissional desativado ganham grupo próprio
  const grupos = useMemo(() => {
    const mapa = new Map(profissionais.map((p) => [p.id, { id: p.id, nome: p.name, itens: [] }]));
    for (const a of doDia) {
      if (!mapa.has(a.user_id)) {
        mapa.set(a.user_id, {
          id: a.user_id,
          nome: `${a.professional_name ?? "Profissional"} (inativo)`,
          itens: [],
        });
      }
      mapa.get(a.user_id).itens.push(a);
    }
    return [...mapa.values()];
  }, [profissionais, doDia]);

  // marca no calendário os dias com bloqueio: "fechado" (dia inteiro que vale para mim/barbearia) ou "parcial"
  const bloqPorDia = useMemo(() => {
    const m = {};
    for (const b of bloqueiosMes) {
      const meu = b.user_id == null || b.user_id === meuId;
      if (!ehAdmin && !meu) continue;                // funcionário: só marca o que vale para ele
      const total = !b.start_time && meu;
      const fim = b.date_to < ate ? b.date_to : ate;
      for (let d = b.date_from > de ? b.date_from : de; d <= fim; d = proximoDia(d))
        m[d] = m[d] === "fechado" || total ? "fechado" : "parcial";
    }
    return m;
  }, [bloqueiosMes, de, ate, meuId, ehAdmin]);

  const bloqueiosDoDia = useMemo(
    () => bloqueiosMes.filter((b) => b.date_from <= selecionadoKey && b.date_to >= selecionadoKey),
    [bloqueiosMes, selecionadoKey]);

  /* navegação */
  const mudarMes = (delta) => setMesAtual(new Date(ano, mes + delta, 1));
  const irParaHoje = () => {
    setMesAtual(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
    setDiaSelecionado(hoje);
  };
  const selecionarDia = (cel) => {
    setDiaSelecionado(cel.date);
    // clicou num dia cinza (mês vizinho)? navega para aquele mês
    if (!cel.currentMonth) setMesAtual(new Date(cel.date.getFullYear(), cel.date.getMonth(), 1));
  };

  /* ----- modal: abrir / fechar ----- */

  const handleChangeAddAg = (field, value) =>
    setFormAddAg((prev) => ({ ...prev, [field]: value }));

  // ao trocar o profissional, o serviço só continua se ele também o realizar
  const handleChangeProfissional = (id) =>
    setFormAddAg((prev) => {
      const prof = profissionais.find((p) => p.id === id);
      const servicoOk = prof?.services?.includes(prev.servico_id);
      return { ...prev, profissional_id: id, servico_id: servicoOk ? prev.servico_id : "" };
    });

  const handleOpen = () => {
    setEditando(null);
    setErroAg("");
    setFormAddAg({
      ...FORM_VAZIO,
      dia: dayjs(diaSelecionado),
      profissional_id: ehAdmin ? "" : meuId ?? "",   // EMPLOYEE só agenda para si
    });
    setOpen(true);
  };

  const abrirEdicao = (a) => {
    if (!a.can_edit) return;
    setEditando(a);
    setErroAg("");
    setFormAddAg({
      nome: a.name,
      descricao: a.obs ?? "",
      telefone: mascaraTelefone(a.phone ?? ""),   // só vem preenchido para ADMIN
      servico_id: a.service_id,
      profissional_id: a.user_id,
      dia: dayjs(a.sched_date),
      hora: horaParaDayjs(a.start_time),
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditando(null);
    setErroAg("");
  };

  /* ----- modal: salvar e desmarcar ----- */

  const salvarAg = async () => {
    const erro = validarAg(formAddAg);
    if (erro) {
      setErroAg(erro);
      return;
    }

    setSalvando(true);
    setErroAg("");
    const dados = {
      user_id: formAddAg.profissional_id,
      service_id: formAddAg.servico_id,
      date: formAddAg.dia.format("YYYY-MM-DD"),
      start_time: formAddAg.hora.format("HH:mm:ss"),
      name: formAddAg.nome.trim(),
      obs: formAddAg.descricao.trim(),
    };
    // EMPLOYEE informa o telefone ao criar, mas não o vê nem o altera depois
    if (ehAdmin || !editando) dados.phone = soDigitos(formAddAg.telefone);

    try {
      if (editando) {
        await axios.post(`${API}/update-schedule`, { id: editando.id, formEditSchedule: dados });
      } else {
        await axios.post(`${API}/register-schedule`, { formAddSchedule: dados });
      }
      // leva o calendário para o dia salvo, para o usuário ver o resultado
      const d = formAddAg.dia.toDate();
      setDiaSelecionado(d);
      setMesAtual(new Date(d.getFullYear(), d.getMonth(), 1));
      setVersao((v) => v + 1);
      handleClose();
    } catch (error) {
      setErroAg(mensagemErro(error, "Erro ao salvar agendamento."));
    } finally {
      setSalvando(false);
    }
  };

  const desmarcar = async () => {
    if (!editando || salvando) return;
    if (!window.confirm(
      `Desmarcar o horário de ${editando.name} às ${hhmm(editando.start_time)}?\n\nO horário ficará livre para novos agendamentos.`
    )) return;

    setSalvando(true);
    setErroAg("");
    try {
      await axios.post(`${API}/cancel-schedule`, { id: editando.id });
      setVersao((v) => v + 1);
      handleClose();
    } catch (error) {
      setErroAg(mensagemErro(error, "Não foi possível desmarcar o horário."));
    } finally {
      setSalvando(false);
    }
  };

  /* ----- bloquear data ----- */

  const carregarBloqueios = () =>
    axios.get(`${API}/time-off`)
      .then(({ data }) => setBloqueios(data))
      .catch((error) => console.error("Erro ao buscar bloqueios:", error));

  const abrirBloqueio = () => {
    setEditandoBloq(null);
    setErroBloq("");
    setOkBloq("");
    setFormBloq({
      ...BLOQUEIO_VAZIO,
      profissional_id: ehAdmin ? "" : meuId ?? "",   // EMPLOYEE só bloqueia a si mesmo
      de: dayjs(diaSelecionado),
    });
    setOpenBloq(true);
    carregarBloqueios();
  };

  // abre o bloqueio para edição (só o dono ou o ADMIN)
  const editarBloqueio = (b) => {
    if (!b.can_delete) return;
    setEditandoBloq(b);
    setErroBloq("");
    setOkBloq("");
    setFormBloq({
      profissional_id: b.user_id ?? "ALL",
      de: dayjs(b.date_from),
      ate: b.date_to !== b.date_from ? dayjs(b.date_to) : null,
      diaInteiro: !b.start_time,
      inicio: b.start_time ? horaParaDayjs(b.start_time) : null,
      fim: b.end_time ? horaParaDayjs(b.end_time) : null,
      motivo: b.reason ?? "",
    });
    setOpenBloq(true);
    carregarBloqueios();
  };

  const fecharBloqueio = () => {
    setOpenBloq(false);
    setEditandoBloq(null);
  };

  const handleChangeBloq = (field, value) => setFormBloq((prev) => ({ ...prev, [field]: value }));

  const salvarBloqueio = async () => {
    const f = formBloq;
    setOkBloq("");
    if (!f.profissional_id) return setErroBloq("Selecione o profissional (ou a empresa inteira).");
    if (!f.de || !f.de.isValid()) return setErroBloq("Informe a data inicial.");
    // um bloqueio em andamento pode ser editado mantendo o início; fora isso, não dá para começar no passado
    const mantemInicio = editandoBloq && f.de.format("YYYY-MM-DD") === editandoBloq.date_from;
    if (!mantemInicio && f.de.isBefore(dayjs(), "day")) return setErroBloq("Não é possível bloquear datas que já passaram.");
    if (f.ate && !f.ate.isValid()) return setErroBloq("Data final inválida.");
    if (f.ate && f.ate.isBefore(f.de, "day")) return setErroBloq("A data final deve ser igual ou depois da inicial.");
    if (!f.diaInteiro) {
      if (!f.inicio?.isValid() || !f.fim?.isValid()) return setErroBloq("Informe o horário de início e de fim.");
      if (!f.inicio.isBefore(f.fim)) return setErroBloq("O horário de fim deve ser depois do início.");
    }

    setSalvandoBloq(true);
    setErroBloq("");
    const dados = {
      date_from: f.de.format("YYYY-MM-DD"),
      date_to: (f.ate ?? f.de).format("YYYY-MM-DD"),
      reason: f.motivo.trim(),
    };
    if (ehAdmin) dados.user_id = f.profissional_id === "ALL" ? null : f.profissional_id;
    if (!f.diaInteiro) {
      dados.start_time = f.inicio.format("HH:mm:ss");
      dados.end_time = f.fim.format("HH:mm:ss");
    }

    try {
      if (editandoBloq) {
        await axios.post(`${API}/update-block`, { id: editandoBloq.id, formBlock: dados });
        fecharBloqueio();
      } else {
        await axios.post(`${API}/block-days`, { formBlock: dados });
        setOkBloq("Bloqueio criado.");
        setFormBloq((prev) => ({ ...BLOQUEIO_VAZIO, profissional_id: prev.profissional_id, de: prev.de }));
      }
      setVersao((v) => v + 1);
      carregarBloqueios();
    } catch (error) {
      setErroBloq(mensagemErro(error, "Erro ao criar o bloqueio."));
    } finally {
      setSalvandoBloq(false);
    }
  };

  const removerBloqueio = async (b) => {
    if (!window.confirm(`Remover o bloqueio de ${b.professional_name} (${dataBR(b.date_from)}${b.date_to !== b.date_from ? ` a ${dataBR(b.date_to)}` : ""})?\n\nOs horários voltam a ficar livres.`)) return;
    try {
      await axios.post(`${API}/unblock-days`, { id: b.id });
      if (editandoBloq?.id === b.id) fecharBloqueio();   // removeu pelo modal de edição
      setOkBloq("Bloqueio removido.");
      setErroBloq("");
      setVersao((v) => v + 1);
      carregarBloqueios();
    } catch (error) {
      setErroBloq(mensagemErro(error, "Não foi possível remover o bloqueio."));
    }
  };

  // no modal, o funcionário vê só os bloqueios dele e os da barbearia inteira
  const bloqueiosVisiveis = bloqueios.filter((b) => ehAdmin || b.user_id == null || b.user_id === meuId);

  /* ----- opções dos selects do modal ----- */

  const opcoesProfissional = [...profissionais];
  if (editando && !profissionais.some((p) => p.id === editando.user_id)) {
    opcoesProfissional.push({ id: editando.user_id, name: `${editando.professional_name} (inativo)`, services: [] });
  }

  const profSel = profissionais.find((p) => p.id === formAddAg.profissional_id);
  const opcoesServico = services.filter((s) => profSel?.services?.includes(s.id));
  if (editando && formAddAg.servico_id && !opcoesServico.some((s) => s.id === formAddAg.servico_id)) {
    opcoesServico.push({ id: editando.service_id, name: editando.service_name ?? "Serviço indisponível" });
  }

  const horaSelecionada = formAddAg.hora?.isValid() ? formAddAg.hora.format("HH:mm") : null;
  const mostraTelefone = ehAdmin || !editando;   // EMPLOYEE não vê o telefone de um agendamento existente

  const IconeDashboard = () => (
    <svg className="sideMenu-btn-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" />
    </svg>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <div className="container-ag">
        <div className="content-ag">
          <div className="sideMenu">
            <div className="sideMenu-content">
              <div className="sideMenu-orientation">
                <div className="sideMenu-top">
                  <img src="/Images/bot-3d.png" className="sideMenu-bot3d" alt="" />
                  <h3 className="sideMenu-name">{nome}</h3>
                </div>
                <div className="sideMenu-btns">
                  <Link className="sideMenu-option" to="/Dashboard">
                    <IconeDashboard />
                    <span className="sideMenu-title">Dashboard</span>
                  </Link>
                  <Link className="sideMenu-option-active" to="/Agendamentos">
                    <img src="/Images/calendar.svg" className="sideMenu-btn-icon" alt="" />
                    <span className="sideMenu-title">Agendamentos</span>
                  </Link>
                  {ehAdmin && (
                    <>
                    <Link className="sideMenu-option" to="/Configuracoes">
                      <img src="/Images/options.svg" className="sideMenu-btn-icon" alt="" />
                      <span className="sideMenu-title">Configuração</span>
                    </Link>
                    <Link className="sideMenu-option" to="/Lancamentos">
                      <img src="/Images/bag.svg" className="sideMenu-btn-icon" alt="" />
                      <span className="sideMenu-title">Lançamentos</span>
                    </Link>
                    </>
                  )}
                </div>
              </div>
              <div className="sideMenu-logout">
                <div className="sideMenu-logout-btn" onClick={() => { logout(); navigate("/"); }}>
                  <img src="/Images/logout.svg" className="sideMenu-logout-icon" alt="" />
                  <span className="sideMenu-title">Sair</span>
                </div>
                <img className="sideMenu-logo" src="/Images/sigmaBranco.png" alt="Sigma Systems" />
              </div>
            </div>
          </div>

          <div className="content">
            <div className="content-top">
              <div className="content-title-wrap">
                <h1 className="content-title">Agendamentos</h1>
                <span className="comissao-mes" title="Sua comissão no mês exibido no calendário">
                  Comissão: {comissaoMes == null ? "—" : brl(comissaoMes)}
                </span>
              </div>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <Button onClick={handleOpen} sx={btnPrimario}>Adicionar</Button>
                <Button onClick={abrirBloqueio} sx={{ ...btnBlock, width: 140 }}>Bloquear Data</Button>
              </div>
            </div>
            <div className="content-block">
              <div className="agenda">
                {/* ===== Calendário ===== */}
                <section className="agenda__calendario">
                  <header className="cal-topo">
                    <h2 style={{ width: "200px", fontSize: "24px" }}>{MESES[mes]} {ano}</h2>
                    <div className="cal-nav">
                      <button onClick={() => mudarMes(-1)} aria-label="Mês anterior">
                        <img src="/Images/arrow.svg" alt="" />
                      </button>
                      <button onClick={() => mudarMes(1)} aria-label="Próximo mês">
                        <img src="/Images/arrow.svg" style={{ rotate: "180deg" }} alt="" />
                      </button>
                    </div>
                    <button className="btn-hoje" onClick={irParaHoje}>Hoje</button>
                  </header>

                  <div className="cal-semana">
                    {DIAS_SEMANA.map((d) => <span key={d}>{d}</span>)}
                  </div>

                  <div className="cal-grade">
                    {grade.map((cel) => {
                      const qtd = contagens[cel.key] ?? 0;
                      const classes = [
                        "cal-dia",
                        !cel.currentMonth && "cal-dia--fora",
                        cel.key === hojeKey && "cal-dia--hoje",
                        cel.key === selecionadoKey && "cal-dia--selecionado",
                        bloqPorDia[cel.key] === "fechado" && "cal-dia--bloqueado",
                      ].filter(Boolean).join(" ");

                      return (
                        <button key={cel.key} className={classes} onClick={() => selecionarDia(cel)}>
                          <span className="cal-dia__numero">{cel.date.getDate()}</span>
                          {bloqPorDia[cel.key] && (
                            <span
                              className={`cal-dia__bloq cal-dia__bloq--${bloqPorDia[cel.key]}`}
                              title={bloqPorDia[cel.key] === "fechado" ? "Dia bloqueado" : "Há bloqueio neste dia"}
                            />
                          )}
                          {qtd > 0 && <span className="cal-dia__badge">{qtd}</span>}
                        </button>
                      );
                    })}
                  </div>
                </section>

                {/* ===== Agenda do dia ===== */}
                <aside className="agenda__dia">
                  <div className="dia-topo">
                    <div>
                      <small>{DIAS_EXTENSO[diaSelecionado.getDay()]}</small>
                      <h3>
                        {String(diaSelecionado.getDate()).padStart(2, "0")} de {MESES[diaSelecionado.getMonth()]}
                      </h3>
                    </div>
                    {selecionadoKey === hojeKey && <span className="tag-hoje">Hoje</span>}
                  </div>

                  <div className="dia-titulo">
                    <h3>Agenda do dia</h3>
                    <small>{doDia.length} {doDia.length === 1 ? "evento" : "eventos"}</small>
                  </div>

                  <input
                    className="dia-busca"
                    type="search"
                    placeholder="Pesquisar..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />

                  <div className="dia-lista">
                    {carregandoDia && <p className="prof__vazio">Carregando...</p>}

                    {/* bloqueio da barbearia inteira */}
                    {bloqueiosDoDia.filter((b) => b.user_id == null).map((b) => (
                      <div
                        key={b.id}
                        className={`bloqueio ${b.can_delete ? "bloqueio--editavel" : ""}`}
                        onClick={b.can_delete ? () => editarBloqueio(b) : undefined}
                        title={b.can_delete ? "Clique para editar o bloqueio" : undefined}
                      >
                        <strong>Estabelecimento fechado</strong> · {textoBloqueio(b)}{b.reason ? ` · ${b.reason}` : ""}
                      </div>
                    ))}

                    {!carregandoDia && grupos.map((g) => {
                      const aberto = !fechados[g.id];
                      const bloqsG = bloqueiosDoDia.filter((b) => b.user_id === g.id);
                      return (
                        <div key={g.id} className="prof">
                          <button
                            className="prof__nome"
                            onClick={() => setFechados((f) => ({ ...f, [g.id]: aberto }))}
                            aria-expanded={aberto}
                          >
                            {g.nome}{" "}
                            {bloqsG.length > 0 && (
                              <span className="tag-bloqueado">
                                {bloqsG.some((b) => !b.start_time) ? "Bloqueado" : "Bloqueio parcial"}
                              </span>
                            )}{" "}
                            <span className={`seta ${aberto ? "seta--aberta" : ""}`}>
                              <img src="/Images/arrow.svg" style={{ rotate: "90deg" }} alt="" />
                            </span>
                          </button>

                          {aberto && bloqsG.map((b) => (
                            <div
                              key={b.id}
                              className={`bloqueio ${b.can_delete ? "bloqueio--editavel" : ""}`}
                              onClick={b.can_delete ? () => editarBloqueio(b) : undefined}
                              title={b.can_delete ? "Clique para editar o bloqueio" : undefined}
                            >
                              {textoBloqueio(b)}{b.reason ? ` · ${b.reason}` : ""}
                            </div>
                          ))}

                          {aberto && (
                            g.itens.length === 0 ? (
                              <p className="prof__vazio">Nenhum agendamento</p>
                            ) : (
                              <ul className="prof__itens">
                                {g.itens.map((a) => (
                                  <li
                                    key={a.id}
                                    className={a.can_edit ? "editavel" : undefined}
                                    onClick={a.can_edit ? () => abrirEdicao(a) : undefined}
                                    title={a.can_edit ? "Clique para editar" : undefined}
                                  >
                                    <span>
                                      <strong>{a.name}</strong>
                                      {a.phone ? ` - ${formatarFone(a.phone)}` : ""} - {a.service_name ?? "Serviço"}
                                      {a.obs ? ` - ${a.obs}` : ""}
                                    </span>
                                    <time>{hhmm(a.start_time)}</time>
                                  </li>
                                ))}
                              </ul>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Modal: adicionar / editar agendamento ===== */}
        <Modal open={open} onClose={handleClose}>
          <Box sx={style}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-header1">
                  <img className="modal-icon" src="/Images/calendar-green.svg" alt="" />
                  <span className="modal-title">
                    {editando ? "Editar Agendamento" : "Adicionar Agendamento"}
                  </span>
                </div>
                <div className="modal-header2">
                  {editando && (
                    <img
                      className="modal-trash-icon"
                      src="/Images/trash.svg"
                      alt="Desmarcar horário"
                      title="Desmarcar horário"
                      onClick={desmarcar}
                    />
                  )}
                </div>
              </div>

              <div className="modal-form">
                <Grid2 container spacing={1} justifyContent="space-between" sx={{ width: "100%" }}>
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <TextField label="Nome" fullWidth variant="outlined" color="success"
                      value={formAddAg.nome}
                      onChange={(e) => handleChangeAddAg("nome", e.target.value)} />
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <TextField label="Descrição" fullWidth variant="outlined" color="success"
                      value={formAddAg.descricao}
                      onChange={(e) => handleChangeAddAg("descricao", e.target.value)} />
                  </Grid2>

                  {mostraTelefone && (
                    <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                      <TextField label="Telefone" fullWidth variant="outlined" color="success"
                        placeholder="(99) 99999-9999"
                        slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 15 } }}
                        value={formAddAg.telefone}
                        onChange={(e) => handleChangeAddAg("telefone", mascaraTelefone(e.target.value))} />
                    </Grid2>
                  )}
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <FormControl fullWidth color="success">
                      <InputLabel>Profissional</InputLabel>
                      <Select label="Profissional" color="success"
                        MenuProps={{ style: { maxHeight: 360 } }}
                        disabled={!ehAdmin}   // EMPLOYEE só mexe nos próprios horários
                        value={formAddAg.profissional_id}
                        onChange={(e) => handleChangeProfissional(e.target.value)}>
                        {opcoesProfissional.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>

                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <FormControl fullWidth color="success" disabled={!formAddAg.profissional_id}>
                      <InputLabel>Serviço</InputLabel>
                      <Select label="Serviço" color="success"
                        MenuProps={{ style: { maxHeight: 360 } }}
                        value={formAddAg.servico_id}
                        onChange={(e) => handleChangeAddAg("servico_id", e.target.value)}>
                        {opcoesServico.length === 0 && (
                          <MenuItem disabled value="">Este profissional não tem serviços</MenuItem>
                        )}
                        {opcoesServico.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 170 } }}>
                    <DateField label="Dia" format="DD-MM-YYYY" color="success" fullWidth
                      value={formAddAg.dia}
                      onChange={(v) => handleChangeAddAg("dia", v)} />
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 170 } }}>
                    <TimeField label="Hora" format="HH:mm" ampm={false} color="success" fullWidth
                      value={formAddAg.hora}
                      onChange={(v) => handleChangeAddAg("hora", v)} />
                  </Grid2>

                  {/* ----- horários livres: clique para preencher a hora ----- */}
                  {formAddAg.profissional_id && formAddAg.servico_id && diaForm && (
                    <Grid2 sx={{ width: "100%" }}>
                      <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>Horários livres</div>
                      {livres.carregando ? (
                        <div style={{ fontSize: 13, color: "#999" }}>Buscando horários...</div>
                      ) : livres.fechado ? (
                        <div style={{ fontSize: 13, color: "#999" }}>O profissional não atende neste dia.</div>
                      ) : livres.horarios.length === 0 ? (
                        <div style={{ fontSize: 13, color: "#999" }}>Sem horários livres para este serviço neste dia.</div>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 96, overflowY: "auto" }}>
                          {livres.horarios.map((t) => (
                            <Chip key={t} size="small" clickable color="success"
                              label={hhmm(t)}
                              variant={hhmm(t) === horaSelecionada ? "filled" : "outlined"}
                              onClick={() => handleChangeAddAg("hora", horaParaDayjs(t))} />
                          ))}
                        </div>
                      )}
                    </Grid2>
                  )}
                </Grid2>

                {erroAg && <Alert severity="error" sx={{ mt: 2 }}>{erroAg}</Alert>}

                <div className="modal-buttons">
                  <Button onClick={salvarAg} disabled={salvando} sx={btnPrimario}>
                    {editando ? "Salvar" : "Adicionar"}
                  </Button>
                  <Button onClick={handleClose} sx={btnSecundario}>Cancelar</Button>
                </div>
              </div>
            </div>
          </Box>
        </Modal>

        {/* ===== Modal: bloquear data ===== */}
        <Modal open={openBloq} onClose={fecharBloqueio}>
          <Box sx={{ ...style, width: { xs: "95vw", md: 640 } }}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-header1">
                  <img className="modal-icon" src="/Images/calendar-green.svg" alt="" />
                  <span className="modal-title">{editandoBloq ? "Editar Bloqueio" : "Bloquear Data"}</span>
                </div>
                <div className="modal-header2">
                  {editandoBloq && (
                    <img
                      className="modal-trash-icon"
                      src="/Images/trash.svg"
                      alt="Remover bloqueio"
                      title="Remover bloqueio"
                      onClick={() => removerBloqueio(editandoBloq)}
                    />
                  )}
                </div>
              </div>

              <div className="modal-form">
                <Grid2 container spacing={1} justifyContent="space-between" sx={{ width: "100%" }}>
                  <Grid2 sx={{ width: "100%" }}>
                    <FormControl fullWidth color="success">
                      <InputLabel>Profissional</InputLabel>
                      <Select label="Profissional" color="success"
                        MenuProps={{ style: { maxHeight: 360 } }}
                        disabled={!ehAdmin}   // EMPLOYEE só bloqueia a si mesmo
                        value={formBloq.profissional_id}
                        onChange={(e) => handleChangeBloq("profissional_id", e.target.value)}>
                        {ehAdmin && <MenuItem value="ALL">Todos</MenuItem>}
                        {profissionais
                          .filter((p) => ehAdmin || p.id === meuId)
                          .map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid2>

                  <Grid2 sx={{ width: { xs: "100%", md: "calc(50% - 4px)" } }}>
                    <DateField label="Data inicial" format="DD-MM-YYYY" color="success" fullWidth
                      value={formBloq.de} onChange={(v) => handleChangeBloq("de", v)} />
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: "calc(50% - 4px)" } }}>
                    <DateField label="Data final (opcional)" format="DD-MM-YYYY" color="success" fullWidth
                      helperText="Vazio = só a data inicial"
                      value={formBloq.ate} onChange={(v) => handleChangeBloq("ate", v)} />
                  </Grid2>

                  <Grid2 sx={{ width: "100%" }}>
                    <FormControlLabel
                      control={
                        <Checkbox color="success" checked={formBloq.diaInteiro}
                          onChange={(e) => handleChangeBloq("diaInteiro", e.target.checked)} />
                      }
                      label="Dia inteiro"
                    />
                  </Grid2>
                  {!formBloq.diaInteiro && (
                    <>
                      <Grid2 sx={{ width: { xs: "100%", md: "calc(50% - 4px)" } }}>
                        <TimeField label="Das" format="HH:mm" ampm={false} color="success" fullWidth
                          value={formBloq.inicio} onChange={(v) => handleChangeBloq("inicio", v)} />
                      </Grid2>
                      <Grid2 sx={{ width: { xs: "100%", md: "calc(50% - 4px)" } }}>
                        <TimeField label="Até" format="HH:mm" ampm={false} color="success" fullWidth
                          value={formBloq.fim} onChange={(v) => handleChangeBloq("fim", v)} />
                      </Grid2>
                    </>
                  )}

                  <Grid2 sx={{ width: "100%" }}>
                    <TextField label="Motivo (opcional)" fullWidth variant="outlined" color="success"
                      slotProps={{ htmlInput: { maxLength: 145 } }}
                      value={formBloq.motivo} onChange={(e) => handleChangeBloq("motivo", e.target.value)} />
                  </Grid2>
                </Grid2>

                {erroBloq && <Alert severity="error" sx={{ mt: 2 }}>{erroBloq}</Alert>}
                {okBloq && <Alert severity="success" sx={{ mt: 2 }}>{okBloq}</Alert>}

                <div className="modal-buttons">
                  <Button onClick={salvarBloqueio} disabled={salvandoBloq} sx={btnPrimario}>
                    {editandoBloq ? "Salvar" : "Bloquear"}
                  </Button>
                  <Button onClick={fecharBloqueio} sx={btnSecundario}>Fechar</Button>
                </div>

                {/* bloqueios que ainda valem */}
                {!editandoBloq && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>
                    {ehAdmin ? "Bloqueios futuros" : "Meus bloqueios futuros"}
                  </div>
                  {bloqueiosVisiveis.length === 0 ? (
                    <div style={{ fontSize: 13, color: "#999" }}>Nenhum bloqueio.</div>
                  ) : (
                    <ul style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: 180, overflowY: "auto" }}>
                      {bloqueiosVisiveis.map((b) => (
                        <li key={b.id} style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          gap: 8, padding: "4px 0", fontSize: 13, borderBottom: "1px solid #eee",
                        }}>
                          <span>
                            <strong>{b.professional_name}</strong>
                            {" · "}{dataBR(b.date_from)}{b.date_to !== b.date_from ? ` a ${dataBR(b.date_to)}` : ""}
                            {" · "}{b.start_time ? `${b.start_time} às ${b.end_time}` : "dia inteiro"}
                            {b.reason ? ` · ${b.reason}` : ""}
                          </span>
                          {b.can_delete && (
                            <span style={{ display: "flex", flex: "none" }}>
                              <Button size="small" color="success" sx={{ textTransform: "none", minWidth: 0 }}
                                onClick={() => editarBloqueio(b)}>Editar</Button>
                              <Button size="small" color="error" sx={{ textTransform: "none", minWidth: 0 }}
                                onClick={() => removerBloqueio(b)}>Remover</Button>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                )}
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </LocalizationProvider>
  );
}

export default Agendamentos;
